import { TClient, TInventory, TUser } from "~/types";
import toNumber from "lodash/toNumber";
import { ContainerType, InventoryStatusCode, InventoryType, JobStatus, PackingTypes, ServiceType } from "./constants";
import dayjs from "dayjs";

export const getClientName = (client?: TClient | TUser) => {
  return client?.firstName && client?.lastName
    ? `${client.firstName} ${client.lastName}`
    : "Unnamed Client";
};
/**
 * Formats dimensions into a standardized string format: length" x width" x height"
 * Works with any object containing width, height, and length properties.
 *
 * @param item - Object containing width, height, and length properties
 * @param options - Configuration options for formatting
 * @returns Formatted dimensions string or fallback value if dimensions are invalid
 */
export const formatDimensions = (
  item:
    | {
        width?: number | string;
        height?: number | string;
        length?: number | string;
      }
    | null
    | undefined,
  options?: {
    unit?: string; // Unit symbol to display (default: ")
    separator?: string; // Separator between dimensions (default: " x ")
    fallback?: string; // Value to return if dimensions are invalid (default: "N/A")
    precision?: number; // Number of decimal places (default: 0)
    showZeroes?: boolean; // Whether to show dimensions that are zero (default: false)
  }
): string => {
  // Default options
  const {
    unit = '"',
    separator = " x ",
    fallback = "N/A",
    precision = 0,
    showZeroes = false,
  } = options || {};

  // Check if item exists
  if (!item) return fallback;

  const { width, height, length } = item;

  // Check if all dimensions are present
  if (
    width === undefined ||
    width === null ||
    height === undefined ||
    height === null ||
    length === undefined ||
    length === null
  ) {
    return fallback;
  }

  // Check if any dimensions are zero and we don't want to show zeroes
  if (!showZeroes && (width === 0 || height === 0 || length === 0)) {
    return fallback;
  }

  // Format the dimensions with the specified precision
  const formattedLength = (length ? toNumber(length) : 0).toFixed(precision);
  const formattedWidth = (width ? toNumber(width) : 0).toFixed(precision);
  const formattedHeight = (height ? toNumber(height) : 0).toFixed(precision);

  // Return the formatted dimensions string in L×W×H format
  return `${formattedLength}${unit}${separator}${formattedWidth}${unit}${separator}${formattedHeight}${unit}H`;
};

function getPluralSuffix(word: string): string {
  // Convert word to lowercase to handle case-insensitive checks
  const lowerWord = word.toLowerCase();

  // Check if word ends with s, sh, ch, x, or z
  if (
    lowerWord.endsWith("s") ||
    lowerWord.endsWith("sh") ||
    lowerWord.endsWith("ch") ||
    lowerWord.endsWith("x") ||
    lowerWord.endsWith("z")
  ) {
    return "es";
  }

  // Default case: add 's'
  return "s";
}
/**
 * Pluralizes a word based on its ending.
 * Adds 's' or 'es' depending on the word's ending.
 *
 * @param word - The word to pluralize
 * @returns The pluralized form of the word
 */
export function pluralize(word: string): string {
  return word + getPluralSuffix(word);
}

export const calculateCubicVolume = (inventory: TInventory): number => {
  const length = toNumber(inventory.length);
  const width = toNumber(inventory.width);
  const height = toNumber(inventory.height);

  if (length > 0 && width > 0 && height > 0) {
    return length * width * height;
  }
  return 0;
};

export const calculateVolumeBK = (inventory: TInventory): number => {
  const length = toNumber(inventory.length);
  const width = toNumber(inventory.width);
  const height = toNumber(inventory.height);

  if (length > 0 && width > 0 && height > 0) {
    // Convert cm³ to m³ (1 cm³ = 0.000001 m³)
    return length * width * height * 0.000001;
  }
  return 0;
};

export const calculateVolumeLI = (inventory: TInventory): number => {
  const length = toNumber(inventory.length);
  const width = toNumber(inventory.width);
  const height = toNumber(inventory.height);

  if (length > 0 && width > 0 && height > 0) {
    return length + width + height;
  }
  return 0;
};

export const compareString = (a?: string, b?: string) => {
  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;
  return a.toLowerCase().localeCompare(b.toLowerCase());
};

export const mapLabel = {
  [ServiceType.COLLECTION]: "Shipper",
  [ServiceType.DELIVERY]: "Consignee",
  [ServiceType.WAREHOUSE_SERVICE]: "Client",
  [ServiceType.FABRICATION_SERVICE]: "Client",
  [ServiceType.ON_SITE_SERVICE]: "Client",
};

export const parseNumber = (value: any) => toNumber(value);

const PACKING_TYPE_LABELS: Record<string, string> = {
  [PackingTypes.LOOSE]: "Loose",
  [PackingTypes.SLIPCASE]: "Slipcase",
  [PackingTypes.CB_FOLD]: "Cardboard Fold",
  [PackingTypes.SHADOW_BOX]: "Shadow Box",
  [PackingTypes.FLOATING_SHADOW_BOX]: "Floating Shadow Box",
  [PackingTypes.POLY_ONLY]: "Poly Only",
  [PackingTypes.TRAVEL_FRAME]: "Travel Frame",
  [PackingTypes.CRATE]: "Crate",
  [PackingTypes.SLATCRATE]: "Slat Crate",
  [PackingTypes.PALLET]: "Pallet",
  [PackingTypes.COMMERCIAL_BIN]: "Commercial Bin",
  [PackingTypes.CBIN]: "C-Bin",
  [PackingTypes.UNKNOWN_PACKAGING]: "Unknown Packaging",
  [PackingTypes.BOX]: "Box",
  [PackingTypes.CUSTOM]: "Custom",
  [PackingTypes.T_Frame]: "T-Frame",
  [PackingTypes.BLANKET_WRAPPED]: "Blanket Wrapped",
  [PackingTypes.POLY_FOAM]: "Poly+Foam",
  [PackingTypes.POLY_FOAM_CB]: "Poly+Foam+CB",
  // Container types (for PACKAGE items)
  [ContainerType.CRATE]: "Crate",
  [ContainerType.PACKAGE]: "Package",
  [ContainerType.PIECE]: "Piece",
  [ContainerType.T_Frame]: "T-Frame",
  [ContainerType.SLIPCASE]: "Slipcase",
  [ContainerType.BOX]: "Box",
  [ContainerType.CUSTOM]: "Custom",
  [ContainerType.SLATCRATE]: "Slat Crate",
  [ContainerType.CBIN]: "C-Bin",
  [ContainerType.PALLET]: "Pallet",
};

/**
 * Returns the display-friendly packing/package type for an inventory item.
 * For PACKAGE items, uses containerType; for PIECE/PART items, uses packingType.
 */
export const formatPackingType = (
  item: { itemType?: InventoryType | string; packingType?: PackingTypes | string; containerType?: ContainerType | string } | null | undefined
): string => {
  if (!item) return "N/A";
  const displayValue = item.itemType === InventoryType.PACKAGE
    ? item.containerType
    : item.packingType;
  if (!displayValue) return "N/A";
  return PACKING_TYPE_LABELS[displayValue] || displayValue.replace(/_/g, " ");
};

export const getTicketStatusColor = (status: JobStatus) => {
  switch (status) {
    case JobStatus.DRAFT:
      return "bg-gray-100 text-gray-800";
    case JobStatus.SCHEDULED:
      return "bg-yellow-100 text-yellow-800";
    case JobStatus.COMPLETED:
      return "bg-green-100 text-green-800";
    case JobStatus.CANCELLED:
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export const getInventoryColor = (status: InventoryStatusCode) => {
  switch (status) {
    case InventoryStatusCode.AVAILABLE_LIVE:
      return "bg-green-100 text-green-800";
    case InventoryStatusCode.AVAILABLE_ON_HAND:
      return "bg-blue-100 text-blue-800";
    case InventoryStatusCode.NO_POSSESSION:
      return "bg-orange-100 text-orange-800";
    case InventoryStatusCode.RELEASED:
      return "bg-purple-100 text-purple-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export const getInventoryColorCode = (status: InventoryStatusCode) => {
  switch (status) {
    case InventoryStatusCode.AVAILABLE_LIVE:
      return "green";
    case InventoryStatusCode.AVAILABLE_ON_HAND:
      return "blue";
    case InventoryStatusCode.NO_POSSESSION:
      return "orange";
    case InventoryStatusCode.RELEASED:
      return "purple";
    default:
      return "default";
  }
};

/**
 * Formats a complete address with all components
 * @param address - The address object to format
 * @returns A formatted string with the complete address information
 */
export const formatFullAddress = (
  address?: {
    address?: string;
    anotherAddress?: string;
    apt?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  } | null
): string => {
  if (!address) return "No address provided";
  const lines: string[] = [];
  if (address.address) {
    lines.push(address.address.trim());
  }
  if (address.apt) {
    lines.push(address.apt.trim());
  }
  const cityStateZipCountry = [address.city, address.state, address.zip, address.country]
    .filter(Boolean)
    .join(", ");
  if (cityStateZipCountry) {
    lines.push(cityStateZipCountry);
  }
  return lines.length > 0 ? lines.join("\n") : "No address provided";
};

/**
 * Calculates the effective service date for warehouse and fabrication services
 * If the service date falls on a weekend and the ticket is not completed,
 * advances the date to the next weekday
 *
 * @param serviceDatetime - Original service date
 * @param dueDate - Due date of the service (used for reference only)
 * @param isCompleted - Whether the service is completed
 * @returns Effective service date (advanced to next weekday if needed)
 */
export const calculateEffectiveServiceDate = (
  serviceDatetime: string | Date | null,
  _dueDate?: string | Date | null,
  isCompleted = false
) => {
  if (!serviceDatetime) return null;

  const serviceDate = dayjs(serviceDatetime);

  // If the service is completed, use the original date regardless of weekday
  if (isCompleted) {
    return serviceDate;
  }

  const dayOfWeek = serviceDate.day();

  // If it's a weekend (0 = Sunday, 6 = Saturday)
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    // Advance to next Monday (1)
    const daysToAdd = dayOfWeek === 0 ? 1 : 2;
    return serviceDate.add(daysToAdd, "day");
  }

  return serviceDate;
};
